/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Icon, isEmpty, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { usePecField } from 'components/form/final-form/useField'
import { ButtonLink, PrivateRoute } from 'components/route'
import { AcessoCbo, SexoEnum } from 'graphql/types.generated'
import React, { useCallback, useMemo } from 'react'
import { Switch, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { MetaPath } from 'util/metaPath'

import AccordionPrescricao from './components/AccordionPrescricao'
import HistoricoPrescricaoTable from './historico-prescricao/HistoricoPrescricaoTable'
import { MedicamentoFormModel } from './MedicamentoFormModel'
import AdicionarPrescricaoModal from './modal/AdicionarPrescricaoModal'

export interface PrescricaoMedicamentoViewProps {
  name: MetaPath<PrescricaoMedicamentoPlanoModel>
  prontuarioId: ID
  atendimentoId: ID
  medicamentos: MedicamentoFormModel[]
  cidadaoDataNascimento: LocalDate
  cidadaoSexo: SexoEnum
}
export interface PrescricaoMedicamentoPlanoModel {
  medicamentos: MedicamentoFormModel[]
  replicateStorage: MedicamentoFormModel[]
}

export default function PrescricaoMedicamentoView({
  name,
  prontuarioId,
  atendimentoId,
  medicamentos,
  cidadaoDataNascimento,
  cidadaoSexo,
}: PrescricaoMedicamentoViewProps) {
  const match = useRouteMatch()
  const hasPrescricao = medicamentos && !isEmpty(medicamentos)

  const {
    input: { onChange: onChangeReplicate, value },
  } = usePecField({ name: name.replicateStorage })

  const handleOnClearReplicate = useCallback(() => onChangeReplicate(null), [onChangeReplicate])

  return useMemo(
    () => (
      <React.Fragment>
        <CheckPermission
          permission={Permissions.visualizarProntuario.soap.plano.visualizarReceitas.cadastrarEditarEExcluir}
        >
          <VFlow
            style={css`
              margin-top: 1rem;
            `}
          >
            <ButtonLink
              kind='primary'
              size='small'
              to={`${match.url}/adicionar`}
              data-testid='PrescricaoMedicamentos.AdicionarPrescricao'
            >
              <Icon
                icon={hasPrescricao ? 'penOutline' : 'plus'}
                style={css`
                  margin-right: 0.5rem;
                `}
              />
              {hasPrescricao ? `Editar prescrição` : `Adicionar prescrição`}
            </ButtonLink>
          </VFlow>
        </CheckPermission>
        <Switch>
          <PrivateRoute
            path={`${match.url}/adicionar`}
            permission={[AcessoCbo.ENFERMEIRO, AcessoCbo.MEDICO, AcessoCbo.CIRURGIAO_DENTISTA]}
          >
            <AdicionarPrescricaoModal
              name={name}
              atendimentoId={atendimentoId}
              cidadaoDataNascimento={cidadaoDataNascimento}
              cidadaoSexo={cidadaoSexo}
              medicamentosToReplicate={value}
              onChangeMedicamentosToReplicate={onChangeReplicate}
              onClearMedicamentosToReplicate={handleOnClearReplicate}
            />
          </PrivateRoute>
        </Switch>
        <VFlow
          style={css`
            margin-top: 1rem;
          `}
        >
          {hasPrescricao && (
            <AccordionPrescricao
              medicamentos={medicamentos}
              atendimentoId={atendimentoId}
              cidadaoDataNascimento={cidadaoDataNascimento}
              cidadaoSexo={cidadaoSexo}
            />
          )}
        </VFlow>
        <VFlow
          style={css`
            margin-top: 1rem;
          `}
        >
          <HistoricoPrescricaoTable prontuarioId={prontuarioId} setMedicamentosToReplicate={onChangeReplicate} />
        </VFlow>
      </React.Fragment>
    ),
    [
      atendimentoId,
      cidadaoDataNascimento,
      cidadaoSexo,
      handleOnClearReplicate,
      hasPrescricao,
      match.url,
      medicamentos,
      name,
      onChangeReplicate,
      prontuarioId,
      value,
    ]
  )
}
